import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { PostCard, type FeedPost } from "@/components/post-card";
import { fetchMapPosts } from "@/lib/posts";
import { getBrowserPosition, haversineKm } from "@/lib/geo";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, MapPin, Navigation, AlertTriangle, Film, Image as ImageIcon, MessageCircle, Siren, Hammer } from "lucide-react";
import { cn } from "@/lib/utils";
import { MediaStrip } from "@/components/media-strip";

export const Route = createFileRoute("/nearby")({
  component: NearbyPage,
  head: () => ({
    meta: [
      { title: "Xamseen — Nearby" },
      { name: "description", content: "Discussions, incidents, projects, and events happening around you." },
    ],
  }),
});

const RADII = [1, 5, 10, 25, 50] as const;
type Radius = (typeof RADII)[number];
type Sort = "recent" | "trending" | "discussed" | "closest" | "supported";

function NearbyPage() {
  const { user } = useAuth();
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null);
  const [denied, setDenied] = useState(false);
  const [radius, setRadius] = useState<Radius>(5);
  const [sort, setSort] = useState<Sort>("recent");
  const [type, setType] = useState<string>("all");
  const [posts, setPosts] = useState<(FeedPost & { latitude: number; longitude: number })[]>([]);
  const [loading, setLoading] = useState(true);

  const requestLocation = async () => {
    const p = await getBrowserPosition();
    if (p) { setPos(p); setDenied(false); } else { setDenied(true); }
  };

  useEffect(() => { requestLocation(); }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setPosts(await fetchMapPosts()); } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!pos) return [];
    const withDist = posts
      .map((p) => ({ ...p, distance: haversineKm(pos, { lat: p.latitude, lng: p.longitude }) }))
      .filter((p) => p.distance <= radius)
      .filter((p) => type === "all" ? true : p.post_type === type);

    switch (sort) {
      case "closest": withDist.sort((a, b) => a.distance - b.distance); break;
      case "trending": withDist.sort((a, b) => (b.support_count ?? 0) - (a.support_count ?? 0)); break;
      case "discussed": withDist.sort((a, b) => b.comment_count - a.comment_count); break;
      case "supported": withDist.sort((a, b) => b.upvotes - a.upvotes); break;
      default: withDist.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    }
    return withDist;
  }, [posts, pos, radius, sort, type]);

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Navigation className="h-4 w-4 text-primary" /> What's happening near me
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Nearby</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Discussions, incidents, emergencies, projects, questions and events around your location.
          </p>
        </header>

        {!pos && !denied && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-card p-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Detecting your location…</p>
          </div>
        )}

        {denied && (
          <div className="mb-6 rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 text-warning" /> Location access needed</div>
            <p className="mt-1 text-sm text-muted-foreground">Allow location to see what's happening around you.</p>
            <Button onClick={requestLocation} size="sm" className="mt-3"><MapPin className="mr-2 h-4 w-4" />Enable location</Button>
          </div>
        )}

        {/* Radius selector */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Radius:</span>
          {RADII.map((r) => (
            <button
              key={r}
              onClick={() => setRadius(r)}
              className={cn("rounded-full border px-3 py-1 text-xs font-medium",
                radius === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground")}
            >{r} km</button>
          ))}
        </div>

        <div className="mb-4 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <Tabs value={sort} onValueChange={(v) => setSort(v as Sort)}>
            <TabsList className="w-max">
              <TabsTrigger value="recent">Recent</TabsTrigger>
              <TabsTrigger value="trending">Trending</TabsTrigger>
              <TabsTrigger value="discussed">Discussed</TabsTrigger>
              <TabsTrigger value="closest">Closest</TabsTrigger>
              <TabsTrigger value="supported">Supported</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>


        <div className="mb-5 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex w-max gap-2">
            {[
              { k: "all", l: "All" },
              { k: "issue", l: "Incidents" },
              { k: "emergency", l: "Emergencies" },
              { k: "project", l: "Projects" },
              { k: "question", l: "Questions" },
              { k: "update", l: "Updates" },
              { k: "discussion", l: "Discussions" },
              { k: "announcement", l: "Events" },
            ].map((t) => (
              <button key={t.k} onClick={() => setType(t.k)}
                className={cn("rounded-full border px-3 py-1.5 text-xs font-medium",
                  type === t.k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground")}>
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {pos && (
          <div className="mb-2">
            <MediaStrip
              title="Nearby Videos"
              icon={<Film className="h-4 w-4 text-primary" />}
              filter="video"
              sort="recent"
              windowDays={30}
              near={{ lat: pos.lat, lng: pos.lng, radiusKm: radius }}
              emptyHint="No videos around you yet."
            />
            <MediaStrip
              title="Nearby Photos"
              icon={<ImageIcon className="h-4 w-4 text-primary" />}
              filter="photo"
              sort="recent"
              windowDays={30}
              near={{ lat: pos.lat, lng: pos.lng, radiusKm: radius }}
              emptyHint="No photos around you yet."
            />
            <MediaStrip
              title="Nearby Emergencies"
              icon={<Siren className="h-4 w-4 text-destructive" />}
              postType="emergency"
              requireMedia={false}
              sort="recent"
              windowDays={14}
              near={{ lat: pos.lat, lng: pos.lng, radiusKm: radius }}
              emptyHint="No active emergencies nearby. Stay safe."
            />
            <MediaStrip
              title="Nearby Projects"
              icon={<Hammer className="h-4 w-4 text-primary" />}
              postType="project"
              requireMedia={false}
              sort="trending"
              windowDays={60}
              near={{ lat: pos.lat, lng: pos.lng, radiusKm: radius }}
              emptyHint="No community projects nearby yet."
            />
            <MediaStrip
              title="Nearby Discussions"
              icon={<MessageCircle className="h-4 w-4 text-primary" />}
              postType="discussion"
              requireMedia={false}
              sort="trending"
              windowDays={30}
              near={{ lat: pos.lat, lng: pos.lng, radiusKm: radius }}
              emptyHint="No discussions around you yet."
            />
          </div>
        )}

        {!pos ? null : loading ? (
          <div className="py-20 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
            <p className="font-display text-base font-medium">No nearby activity</p>
            <p className="mt-1 text-sm text-muted-foreground">Try expanding the radius or be the first to post something here.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((p) => (
              <div key={p.id} className="relative">
                <PostCard post={p as any} />
                <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-border">
                  {p.distance < 1 ? `${Math.round(p.distance * 1000)} m` : `${p.distance.toFixed(1)} km`}
                </span>
              </div>
            ))}
          </div>
        )}

        {!user && (
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Sign in to post incidents, projects and questions in your area.
          </p>
        )}
      </div>
    </AppShell>
  );
}
